import type {Price} from '@/redux/v1/models';
import type {ISO8601Date} from '@/types';

export const FILTER_PRICE = 'price' as const;
export const FILTER_DEPARTURE_TIME = 'departureTime' as const;
export const FILTER_RETURN_TIME = 'returnTime' as const;
export const FILTER_FLIGHT_TRANSFER = 'flightTransfer' as const;
export const FILTER_BOARD_TYPE = 'boardType' as const;
export const FILTER_ROOM_TYPE = 'roomType' as const;
export const FILTER_TOUR_OPERATOR = 'tourOperator' as const;
export const FILTER_DURATION = 'duration' as const;
export const FILTER_PROMOTION_SPECIALS = 'specials' as const;
export const FILTER_PROMOTION_FLEX = 'flex' as const;
export const FILTER_PROMOTION_SAFETY = 'safety' as const;
export const FILTER_FACILITIES = 'facilities' as const;
export const FILTER_CHILDREN = 'children' as const;
export const FILTER_WELLNESS = 'wellness' as const;
export const FILTER_POOL = 'pool' as const;
export const FILTER_BEACH = 'beach' as const;
export const FILTER_SPORTS = 'sports' as const;
export const FILTER_WATER_SPORTS = 'watersports' as const;
export const FILTER_RESTAURANT = 'restaurant' as const;
export const FILTER_ENTERTAINMENT = 'entertainment' as const;
export const FILTER_HOTEL_CATEGORY = 'category' as const;
export const FILTER_HOTEL_RATING = 'rating' as const;
export const FILTER_PRICE_CHART = 'priceChart' as const;
export const FILTER_HOTEL_TYPE = 'hotelType' as const;
export const FILTER_SUSTAINABILITY = 'sustainability' as const;
export const FILTER_AIRLINES = 'airlines' as const;
export const FILTER_ROOM_ATTRIBUTES = 'roomAttributes' as const;
export const FILTER_DESTINATION_AIRPORTS = 'arrivalAirports' as const;

type AbstractFilter<Id, Value> = {
  id: Id;
  value: Value;
  active: boolean;
  available: boolean;
  title: string;
  setValue: (value: Value) => void;
  clear: () => void;
};

export type OptionFilterItem<T = string> = {
  active: boolean;
  available: boolean;
  value: T;
  label: string;
  description?: string;
  count?: number;
  priceMin?: Price;
  subLabel?: string;
  subLabelDescripion?: string;
};

type RangeFilter<Id, Value = [number, number]> = AbstractFilter<Id, Value>;

type SingleOptionFilter<Id, Value> = AbstractFilter<Id, Value> & {
  options: Array<OptionFilterItem>;
};

type MultiOptionFilter<Id> = AbstractFilter<Id, Array<string>> & {
  options: Array<OptionFilterItem>;
};

// Static
type PriceFilter = AbstractFilter<
  typeof FILTER_PRICE,
  [Price | null, Price | null]
> & {
  priceMin: Price;
};
type DepartureTimeFilter = RangeFilter<typeof FILTER_DEPARTURE_TIME>;
type ReturnTimeFilter = RangeFilter<typeof FILTER_RETURN_TIME>;
type PriceChartFilter = AbstractFilter<
  typeof FILTER_PRICE_CHART,
  {
    startDate: ISO8601Date;
    endDate: ISO8601Date;
    duration: number;
  }
>;

export type StaticFilters =
  | PriceFilter
  | DepartureTimeFilter
  | ReturnTimeFilter
  | PriceChartFilter;

// Promotion
type SpecialsFilter = MultiOptionFilter<typeof FILTER_PROMOTION_SPECIALS>;
type FlexFilter = MultiOptionFilter<typeof FILTER_PROMOTION_FLEX>;
type SafetyFilter = MultiOptionFilter<typeof FILTER_PROMOTION_SAFETY>;

type PromotionFilters = SpecialsFilter | FlexFilter | SafetyFilter;

type RoomAttributesFilter = MultiOptionFilter<typeof FILTER_ROOM_ATTRIBUTES>;

// Inventory
type RoomTypeFilter = MultiOptionFilter<typeof FILTER_ROOM_TYPE>;
type BoardTypeFilter = MultiOptionFilter<typeof FILTER_BOARD_TYPE>;
type TourOperatorFilter = MultiOptionFilter<typeof FILTER_TOUR_OPERATOR>;
type DurationFilter = SingleOptionFilter<typeof FILTER_DURATION, number>;
type TransferFilter = MultiOptionFilter<typeof FILTER_FLIGHT_TRANSFER>;
type AirlinesFilter = MultiOptionFilter<typeof FILTER_AIRLINES>;
type ArrivalAirportsFilter = MultiOptionFilter<
  typeof FILTER_DESTINATION_AIRPORTS
>;

export type InventoryFilters =
  | RoomTypeFilter
  | BoardTypeFilter
  | TourOperatorFilter
  | DurationFilter
  | TransferFilter
  | AirlinesFilter
  | ArrivalAirportsFilter;

// Meta
export type HotelCategoryFilter = SingleOptionFilter<
  typeof FILTER_HOTEL_CATEGORY,
  number
>;
export type HotelRatingFilter = SingleOptionFilter<
  typeof FILTER_HOTEL_RATING,
  string
>;
type FacilitiesFilter = MultiOptionFilter<typeof FILTER_FACILITIES>;
type SustainabilityFilter = MultiOptionFilter<typeof FILTER_SUSTAINABILITY>;
type ChildrenFilter = MultiOptionFilter<typeof FILTER_CHILDREN>;
type WellnessFilter = MultiOptionFilter<typeof FILTER_WELLNESS>;
type PoolFilter = MultiOptionFilter<typeof FILTER_POOL>;
type BeachFilter = MultiOptionFilter<typeof FILTER_BEACH>;
type SportsFilter = MultiOptionFilter<typeof FILTER_SPORTS>;
type WaterSportsFilter = MultiOptionFilter<typeof FILTER_WATER_SPORTS>;
type RestaurantFilter = MultiOptionFilter<typeof FILTER_RESTAURANT>;
type EntertainmentFilter = MultiOptionFilter<typeof FILTER_ENTERTAINMENT>;
type HotelTypeFilter = MultiOptionFilter<typeof FILTER_HOTEL_TYPE>;
export type MetaFilters =
  | HotelCategoryFilter
  | SustainabilityFilter
  | HotelRatingFilter
  | FacilitiesFilter
  | ChildrenFilter
  | WellnessFilter
  | PoolFilter
  | BeachFilter
  | SportsFilter
  | WaterSportsFilter
  | RestaurantFilter
  | EntertainmentFilter
  | HotelTypeFilter
  | AirlinesFilter
  | ArrivalAirportsFilter;

export type Filter =
  | MetaFilters
  | InventoryFilters
  | StaticFilters
  | PromotionFilters
  | RoomAttributesFilter;

export const filtersWithOptionCount = new Set<string>([
  FILTER_FACILITIES,
  FILTER_CHILDREN,
  FILTER_WELLNESS,
  FILTER_POOL,
  FILTER_BEACH,
  FILTER_SPORTS,
  FILTER_WATER_SPORTS,
  FILTER_RESTAURANT,
  FILTER_ENTERTAINMENT,
  FILTER_HOTEL_TYPE,
]);

export const filtersWithBestPrice = new Set<string>([
  FILTER_BOARD_TYPE,
  FILTER_ROOM_TYPE,
  FILTER_TOUR_OPERATOR,
  FILTER_FACILITIES,
  FILTER_CHILDREN,
  FILTER_WELLNESS,
  FILTER_POOL,
  FILTER_BEACH,
  FILTER_SPORTS,
  FILTER_WATER_SPORTS,
  FILTER_RESTAURANT,
  FILTER_ENTERTAINMENT,
  FILTER_HOTEL_CATEGORY,
  FILTER_HOTEL_RATING,
  FILTER_HOTEL_TYPE,
  FILTER_SUSTAINABILITY,
  FILTER_AIRLINES,
  FILTER_ROOM_ATTRIBUTES,
  FILTER_DESTINATION_AIRPORTS,
]);
